/**
 * @file audit-view.ts
 * @description Раскладка «Сайт → SEO → Проверка сайта» (phase19.16, партия
 * Р3в, v0.19.64; SB132, SEO12; uiux-r3 §3.5; контракт r3v-api-contract §6):
 * замечания сервера (плоский список) собираются в группы по коду — красная
 * полоса, предупреждения (!), сведения (ⓘ) и строки «всё хорошо» (✓).
 *
 * Тексты групп — из shared `SITE_SEO_ISSUES` (один реестр с api); строки
 * «всё хорошо» — реестр `SEO_OK_LINES`; пояснение строки по `details` —
 * реестр `NOTE_BY_CODE`; «Исправить →» — путь кабинета магазина
 * (`/shop/<slug>` + `fixUrl` сервера; страница и меню — ссылкой в
 * конструктор). Чистые функции — проверяют тесты.
 */

import { SITE_SEO_ISSUE_CODES, SITE_SEO_ISSUES, type SiteSeoIssueCode, type SiteSeoIssueLevel } from "@chaiberi/shared";

import type { SiteSeoAuditDto, SiteSeoIssueDto } from "../api-site-seo";
import { pluralRu } from "../plural";
import { builderLink } from "../site-builder/selection";

/** «Всё хорошо» по коду, когда замечаний этого вида нет. */
export const SEO_OK_LINES: Readonly<Partial<Record<SiteSeoIssueCode, string>>> = {
  seo_h1_count: "На каждой странице один главный заголовок",
  seo_title_duplicate: "Заголовки для поиска не повторяются",
  seo_image_alt_missing: "У фото на страницах есть описания",
  seo_link_broken: "Внутренние ссылки ведут на живые страницы",
  site_menu_target_hidden: "Пункты меню ведут на видимые страницы",
};

/** Строка группы: о чём замечание, пояснение и куда вести. */
export interface AuditRow {
  key: string;
  title: string;
  note: string | null;
  href: string | null;
}

export interface AuditGroup {
  code: SiteSeoIssueCode;
  level: SiteSeoIssueLevel;
  title: string;
  hint: string | null;
  rows: AuditRow[];
}

export interface AuditLayout {
  /** Сайт закрыт от поисковиков — строка-замечание первой в предупреждениях (полоса — в кабинете). */
  banner: boolean;
  warnings: AuditGroup[];
  infos: AuditGroup[];
  /** Строки ✓. */
  ok: string[];
  /** Замечаний нет вовсе. */
  clean: boolean;
}

const num = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);
const str = (value: unknown): string | null => (typeof value === "string" && value ? value : null);

/** «74 знака (лучше до 60)». */
function lengthNote(d: Record<string, unknown>, fallbackLimit: number): string | null {
  const length = num(d.length);
  if (length === null) return null;
  return `${length} ${pluralRu(length, ["знак", "знака", "знаков"])} (лучше до ${num(d.limit) ?? fallbackLimit})`;
}

/** Где стоит пункт меню (`details.menuLocation`): шапка, подвал, меню телефона. */
const MENU_LOCATION_TEXT: Readonly<Record<string, string>> = { header: "шапка", footer: "подвал", phone: "меню телефона" };

/** «шапка» по `details.menuLocation` (регистр не важен); нет — null. */
function menuLocationNote(d: Record<string, unknown>): string | null {
  const location = str(d.menuLocation)?.toLowerCase();
  return location ? (MENU_LOCATION_TEXT[location] ?? null) : null;
}

/** Пояснение строки по `details` сервера (реестр: код → фраза). */
const NOTE_BY_CODE: Readonly<Partial<Record<SiteSeoIssueCode, (d: Record<string, unknown>) => string | null>>> = {
  seo_title_long: (d) => lengthNote(d, 60),
  seo_description_long: (d) => lengthNote(d, 160),
  seo_title_duplicate: (d) => (str(d.title) ? `«${str(d.title)}»` : null),
  seo_h1_count: (d) => (num(d.count) !== null ? `главных заголовков: ${num(d.count)}` : null),
  seo_image_alt_missing: (d) => (num(d.count) !== null ? `фото без описания: ${num(d.count)}` : null),
  seo_link_broken: (d) => [menuLocationNote(d), str(d.href) ? `ссылка ${str(d.href)}` : null].filter(Boolean).join(" · ") || null,
  site_menu_target_hidden: (d) => menuLocationNote(d),
  seo_link_via_redirect: (d) => (str(d.href) ? `${str(d.href)} → ${str(d.location) ?? "…"}` : null),
  seo_redirects_inactive: (d) => (num(d.count) !== null ? `не действуют: ${num(d.count)}` : null),
  seo_keyword_missing: (d) => (str(d.keyword) ? `запрос «${str(d.keyword)}»` : null),
  seo_keyword_duplicate: (d) => (str(d.keyword) ? `запрос «${str(d.keyword)}»` : null),
};

/** «Исправить →»: страница и меню — в конструктор, остальное — путь сервера внутри магазина. */
export function auditFixHref(issue: SiteSeoIssueDto, shopSlug: string): string | null {
  if (issue.subject.kind === "page" && issue.subject.id) return builderLink(shopSlug, { pageId: issue.subject.id });
  // Меню: сервер знает место пункта (`panel=menu.footer`); без него — меню шапки.
  if (issue.subject.kind === "menu" && !issue.fixUrl?.includes("panel=")) return builderLink(shopSlug, { panel: "menu", sub: "header" });
  // Отдельной страницы категории в кабинете нет — дерево категорий (правка — окном).
  if (issue.subject.kind === "category") return `/shop/${shopSlug}/catalog/categories`;
  if (!issue.fixUrl) return null;
  return `/shop/${shopSlug}${issue.fixUrl.startsWith("/") ? issue.fixUrl : `/${issue.fixUrl}`}`;
}

/** Группы по коду в порядке реестра shared; уровень — из реестра (запасной — сервера). */
export function auditLayout(audit: SiteSeoAuditDto, shopSlug: string): AuditLayout {
  const byCode = new Map<SiteSeoIssueCode, SiteSeoIssueDto[]>();
  for (const issue of audit.issues) {
    const list = byCode.get(issue.code) ?? [];
    list.push(issue);
    byCode.set(issue.code, list);
  }
  const order = [...SITE_SEO_ISSUE_CODES, ...[...byCode.keys()].filter((c) => !SITE_SEO_ISSUE_CODES.includes(c))];
  const groups: AuditGroup[] = order
    .filter((code) => byCode.has(code))
    .map((code) => {
      const issues = byCode.get(code) ?? [];
      const meta = SITE_SEO_ISSUES[code];
      return {
        code,
        level: meta?.level ?? issues[0]?.level ?? "warning",
        title: meta?.title ?? code,
        hint: meta?.hint ?? null,
        rows: issues.map((issue, index) => ({
          // Один объект может дать несколько строк (две ссылки меню) — ключ с номером.
          key: `${issue.subject.kind}:${issue.subject.id ?? "-"}:${index}`,
          title: issue.subject.title,
          // Место пункта меню — в `subject.menuLocation` (контракт §6), запасное — в `details`.
          note: NOTE_BY_CODE[code]?.({ ...issue.details, menuLocation: issue.subject.menuLocation ?? issue.details?.menuLocation }) ?? null,
          href: auditFixHref(issue, shopSlug),
        })),
      };
    });
  const banner = audit.banner === "seo_site_noindex" || audit.issues.some((i) => i.code === "seo_site_noindex");
  const ok = Object.entries(SEO_OK_LINES)
    .filter(([code]) => !byCode.has(code as SiteSeoIssueCode))
    .map(([, line]) => line as string);
  return {
    banner,
    warnings: groups.filter((g) => g.level === "warning"),
    infos: groups.filter((g) => g.level === "info"),
    ok,
    clean: groups.length === 0,
  };
}
